// src/components/CoffeeBlends.jsx
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from '../shadcn/ui/table';
import { Button } from '../shadcn/ui/button';
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from '../shadcn/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../shadcn/ui/dialog';
import { Input } from '../shadcn/ui/input';
import { Label } from '../shadcn/ui/label';
import { Textarea } from '../shadcn/ui/textarea';
import { EyeIcon, EyeSlashIcon, PencilIcon, TrashIcon, ArrowTopRightOnSquareIcon, PlusIcon, XMarkIcon, EllipsisVerticalIcon } from '@heroicons/react/24/outline';
import ProductForm from './ProductForm';
import DeleteConfirmationDialog from './DeleteConfirmationDialog';
import MediaViewDialog from './Dialog/MediaViewDialog';
import SkeletonLoader from './SkeletonLoader';
import MediaDialog from './MediaDialog';
import { toast } from '@/shadcn/ui/use-toast';
import { Checkbox } from '../shadcn/ui/checkbox';
import axios from '../axios';
import config from '../config';
import { useLocation } from 'react-router-dom';
const CATEGORY_CHILD_KEYS = ['children', 'children_recursive', 'childrenRecursive'];
const PARENT_KEYS = ['parent', 'parent_category', 'parentCategory'];
const transformPathValue = (value, key) => {
  if (!value) return [];
  if (Array.isArray(value)) {
    return value
      .map((node) => {
        if (typeof node === 'string') {
          return node;
        }
        if (node && typeof node === 'object') {
          return node[key] ?? node.slug ?? node.name ?? '';
        }
        return '';
      })
      .filter(Boolean);
  }
  if (typeof value === 'string') {
    return value.split('/').filter(Boolean);
  }
  return [];
};

const resolveParentCategory = (category) => {
  if (!category) return null;
  for (const key of PARENT_KEYS) {
    if (category[key]) {
      return category[key];
    }
  }
  return null;
};

const buildCategoryPathFromCategory = (category, key = 'name') => {
  if (!category) return [];
  const candidateKeys = ['ancestors', 'path', 'paths'];
  for (const candidateKey of candidateKeys) {
    const segments = transformPathValue(category[candidateKey], key);
    if (segments.length) {
      const currentValue = category[key] ?? (key === 'slug' ? category.slug : category.name);
      if (currentValue && segments[segments.length - 1] !== currentValue) {
        return [...segments, currentValue];
      }
      return segments;
    }
  }

  const visited = new Set();
  const path = [];
  let current = category;
  while (current && !visited.has(current.id || current.slug || current.name)) {
    visited.add(current.id || current.slug || current.name);
    const value = current[key] ?? (key === 'slug' ? current.slug : current.name);
    if (value) {
      path.unshift(value);
    }
    current = resolveParentCategory(current);
  }
  return path;
};

const getBlendCategoryPath = (blend, key = 'name') => {
  if (!blend) return [];
  const specificPath = transformPathValue(blend[`category_${key}_path`], key);
  if (specificPath.length) {
    return specificPath;
  }
  const genericPath = transformPathValue(blend.category_path, key);
  if (genericPath.length) {
    return genericPath;
  }
  return buildCategoryPathFromCategory(blend.category, key);
};

const normalizeCategoriesResponse = (data) => {
  if (!data) return [];
  if (Array.isArray(data)) return data;
  if (Array.isArray(data.data)) return data.data;
  if (Array.isArray(data.categories)) return data.categories;
  return [];
};

const normalizeCategoryTreeResponse = (data) => {
  if (!data) return [];
  if (Array.isArray(data)) return data;
  if (Array.isArray(data.data)) return data.data;
  if (Array.isArray(data.tree)) return data.tree;
  return [];
};

const getChildNodes = (node) => {
  if (!node) return [];
  for (const key of CATEGORY_CHILD_KEYS) {
    if (Array.isArray(node[key])) {
      return node[key];
    }
  }
  return [];
};

const normalizeCategoryNode = (node, trail = []) => {
  if (!node) return null;
  const normalized = {
    ...node,
    id: String(node.id),
    parent_id:
      node.parent_id === undefined || node.parent_id === null ? null : String(node.parent_id),
  };

  const currentTrail = [...trail, { id: normalized.id, name: normalized.name, slug: normalized.slug }];
  normalized.pathNames = currentTrail.map((item) => item.name).filter(Boolean);
  normalized.pathSlugs = currentTrail.map((item) => item.slug).filter(Boolean);

  const children = getChildNodes(node)
    .map((child) => normalizeCategoryNode(child, currentTrail))
    .filter(Boolean);

  normalized.children = children;
  return normalized;
};

const findCategoryInTree = (nodes, id) => {
  if (!id || !Array.isArray(nodes)) return null;
  for (const node of nodes) {
    if (String(node.id) === String(id)) {
      return node;
    }
    const found = findCategoryInTree(node.children || [], id);
    if (found) {
      return found;
    }
  }
  return null;
};

const getCategoryDisplayPath = (blend) => {
  const names = getBlendCategoryPath(blend, 'name');
  return names.join(' / ');
};

const flattenCategoryOptions = (nodes = [], trail = []) => {
  const options = [];
  nodes.forEach((node) => {
    if (!node) return;
    const pathNames =
      node.pathNames && node.pathNames.length
        ? node.pathNames
        : [...trail, node.name].filter(Boolean);
    const label = pathNames.length ? pathNames.join(' / ') : node.name || `Κατηγορία #${node.id}`;
    options.push({
      id: String(node.id),
      label,
      pathNames,
    });
    const children = Array.isArray(node.children) ? node.children : [];
    options.push(...flattenCategoryOptions(children, pathNames));
  });
  return options;
};

const QUICK_FILTER_ROOT_VALUE = '__root__filter__';
const ROOT_PARENT_VALUE = '__root__parent__';

const flattenTreeToList = (nodes = []) => {
  const list = [];
  nodes.forEach((node) => {
    if (!node) return;
    list.push({
      id: String(node.id),
      name: node.name,
      parent_id: node.parent_id,
      slug: node.slug,
    });
    if (Array.isArray(node.children) && node.children.length > 0) {
      list.push(...flattenTreeToList(node.children));
    }
  });
  return list;
};

const CategoryTreeNode = ({
  node,
  depth = 0,
  selectedId,
  onSelect,
  onAddChild,
  onEdit,
  onDelete,
}) => {
  const isSelected = selectedId === node.id;
  const childNodes = node.children || [];

  return (
    <div className="space-y-2" style={{ marginLeft: depth * 12 }}>
      <div
        className={`flex items-center justify-between rounded border p-2 text-sm ${
          isSelected ? 'border-emerald-500 bg-emerald-50' : 'border-gray-200 bg-white'
        }`}
      >
        <button
          type="button"
          className="flex-1 text-left font-medium text-gray-800"
          onClick={() => onSelect(node)}
        >
          {node.name}
        </button>
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="sm"
            className="h-8 w-8 p-0"
            onClick={() => onAddChild(node)}
            title="Προσθήκη υποκατηγορίας"
          >
            <PlusIcon className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="h-8 w-8 p-0"
            onClick={() => onEdit(node)}
            title="Επεξεργασία"
          >
            <PencilIcon className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="h-8 w-8 p-0"
            onClick={() => onDelete(node)}
            title="Διαγραφή"
            disabled={node.children && node.children.length > 0}
          >
            <TrashIcon className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {childNodes.length > 0 && (
        <div className="space-y-2">
          {childNodes.map((child) => (
            <CategoryTreeNode
              key={child.id}
              node={child}
              depth={depth + 1}
              selectedId={selectedId}
              onSelect={onSelect}
              onAddChild={onAddChild}
              onEdit={onEdit}
              onDelete={onDelete}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const CategoryModal = ({
  isOpen,
  mode,
  onClose,
  onSubmit,
  parentId,
  initialValues,
  categories,
  isSubmitting,
  rootCategoryId,
  allowRootSelection = false,
}) => {
  const [formState, setFormState] = useState({
    name: '',
    parent_id: '',
    small_description: '',
    large_description: '',
  });
  const [selectedMedia, setSelectedMedia] = useState(null);
  const [isMediaDialogOpen, setIsMediaDialogOpen] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (isOpen) {
      const normalizedParent =
        initialValues?.parent_id === null || initialValues?.parent_id === undefined
          ? parentId ?? rootCategoryId ?? ''
          : String(initialValues?.parent_id);

      setFormState({
        name: initialValues?.name || '',
        parent_id:
          normalizedParent === null || normalizedParent === undefined ? '' : normalizedParent,
        small_description: initialValues?.description?.small_description || '',
        large_description: initialValues?.description?.large_description || '',
      });
      setSelectedMedia(initialValues?.media || null);
      setErrors({});
    }
  }, [initialValues, isOpen, parentId, rootCategoryId]);

  const availableParents = useMemo(() => {
    const currentId = initialValues?.id ? String(initialValues.id) : null;
    return categories.filter((category) => category.id !== currentId);
  }, [categories, initialValues?.id]);

  useEffect(() => {
    if (!allowRootSelection && !formState.parent_id && availableParents.length) {
      setFormState((prev) => ({ ...prev, parent_id: availableParents[0].id }));
    }
  }, [availableParents, formState.parent_id, allowRootSelection]);

  const handleChange = (field, value) => {
    setFormState((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  const handleMediaSelect = (mediaItems) => {
    const media = mediaItems?.[0];
    if (media) {
      setSelectedMedia(media);
    }
    setIsMediaDialogOpen(false);
  };

  const handleRemoveMedia = () => {
    setSelectedMedia(null);
  };

  const validate = () => {
    const validationErrors = {};
    if (!formState.name.trim()) {
      validationErrors.name = 'Το όνομα είναι υποχρεωτικό.';
    }
    setErrors(validationErrors);
    return Object.keys(validationErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) return;

    const resolvedParentId =
      formState.parent_id && formState.parent_id !== ''
        ? Number(formState.parent_id)
        : allowRootSelection
          ? null
          : rootCategoryId
            ? Number(rootCategoryId)
            : null;

    const payload = {
      name: formState.name.trim(),
      parent_id: resolvedParentId,
      media_id: selectedMedia?.id ?? null,
      description: {
        small_description: formState.small_description,
        large_description: formState.large_description,
      },
    };

    onSubmit(payload);
  };

  return (
    <>
      <Dialog open={isOpen} onOpenChange={(open) => (!open ? onClose() : null)}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>{mode === 'edit' ? 'Επεξεργασία κατηγορίας' : 'Νέα κατηγορία'}</DialogTitle>
            <DialogDescription>
              Συμπληρώστε τα πεδία για να {mode === 'edit' ? 'ενημερώσετε' : 'δημιουργήσετε'} μια κατηγορία.
            </DialogDescription>
          </DialogHeader>

          <form className="space-y-4" onSubmit={handleSubmit}>
            <div className="space-y-2">
              <Label htmlFor="category-name">Όνομα</Label>
              <Input
                id="category-name"
                value={formState.name}
                onChange={(e) => handleChange('name', e.target.value)}
                placeholder="Π.χ. Coffees"
                className={errors.name ? 'border-red-500' : ''}
              />
              {errors.name && <p className="text-sm text-red-600">{errors.name}</p>}
            </div>

            <div className="space-y-2">
              <Label>Γονική κατηγορία</Label>
              <Select
                value={
                  formState.parent_id
                    ? String(formState.parent_id)
                    : allowRootSelection
                      ? ROOT_PARENT_VALUE
                      : ''
                }
                onValueChange={(value) =>
                  handleChange(
                    'parent_id',
                    allowRootSelection && value === ROOT_PARENT_VALUE ? '' : value
                  )
                }
                disabled={!allowRootSelection && !availableParents.length}
              >
                <SelectTrigger>
                  <SelectValue placeholder={allowRootSelection ? 'Χωρίς γονική (κύρια)' : 'Επιλέξτε γονική κατηγορία'} />
                </SelectTrigger>
                <SelectContent>
                  {allowRootSelection && (
                    <SelectItem value={ROOT_PARENT_VALUE}>Χωρίς γονική (κύρια)</SelectItem>
                  )}
                  {availableParents.length ? (
                    availableParents.map((category) => (
                      <SelectItem key={category.id} value={category.id}>
                        {category.name}
                      </SelectItem>
                    ))
                  ) : (
                    <SelectItem value="no-parents" disabled>
                      Δεν υπάρχουν διαθέσιμες γονικές
                    </SelectItem>
                  )}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="category-small-description">Μικρή περιγραφή</Label>
              <Textarea
                id="category-small-description"
                value={formState.small_description}
                onChange={(e) => handleChange('small_description', e.target.value)}
                rows={3}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="category-large-description">Μεγάλη περιγραφή</Label>
              <Textarea
                id="category-large-description"
                value={formState.large_description}
                onChange={(e) => handleChange('large_description', e.target.value)}
                rows={5}
              />
            </div>

            <div className="space-y-2">
              <Label>Εικόνα</Label>
              {selectedMedia ? (
                <div className="flex items-center gap-3">
                  <img
                    src={selectedMedia.full_file_path || selectedMedia.file_path}
                    alt={selectedMedia.name || 'media'}
                    className="h-16 w-16 rounded object-cover"
                  />
                  <div className="flex gap-2">
                    <Button variant="outline" type="button" onClick={() => setIsMediaDialogOpen(true)}>
                      Αλλαγή
                    </Button>
                    <Button variant="outline" type="button" onClick={handleRemoveMedia}>
                      Αφαίρεση
                    </Button>
                  </div>
                </div>
              ) : (
                <Button variant="outline" type="button" onClick={() => setIsMediaDialogOpen(true)}>
                  Επιλογή εικόνας
                </Button>
              )}
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={onClose} disabled={isSubmitting}>
                Άκυρο
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {mode === 'edit' ? 'Αποθήκευση' : 'Δημιουργία'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <MediaDialog
        isOpenMediaDialog={isMediaDialogOpen}
        onCloseMediaDialog={() => setIsMediaDialogOpen(false)}
        onSelectMedia={handleMediaSelect}
        maxSelection={1}
      />
    </>
  );
};

const CoffeeBlends = ({
  rootCategoryId = '',
  rootSlug = 'coffees',
  pageTitleOverride,
  pageDescriptionOverride,
}) => {
  const location = useLocation();
  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const isCategoriesOnly = useMemo(() => searchParams.get('view') === 'categories', [searchParams]);
  const rootQueryParam = useMemo(() => searchParams.get('root'), [searchParams]);

  const [coffeeBlends, setCoffeeBlends] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingBlend, setEditingBlend] = useState(null);
  const [deleteBlendId, setDeleteBlendId] = useState(null);
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const [selectedMedia, setSelectedMedia] = useState(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const itemsPerPage = 10;

  const [categoriesTree, setCategoriesTree] = useState([]);
  const [flatCategories, setFlatCategories] = useState([]);
  const [categoryLoading, setCategoryLoading] = useState(false);
  const [selectedCategoryId, setSelectedCategoryId] = useState('');
  const [activeCategoryId, setActiveCategoryId] = useState('');

  const [isCategoryModalOpen, setIsCategoryModalOpen] = useState(false);
  const [categoryModalMode, setCategoryModalMode] = useState('create');
  const [categoryModalInitial, setCategoryModalInitial] = useState(null);
  const [categoryModalParentId, setCategoryModalParentId] = useState(null);
  const [isCategorySubmitting, setIsCategorySubmitting] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState(null);
  const [isCategoryPanelOpen, setIsCategoryPanelOpen] = useState(false);
  const [resolvedRootCategoryId, setResolvedRootCategoryId] = useState(
    rootQueryParam ? String(rootQueryParam) : rootCategoryId ? String(rootCategoryId) : ''
  );
  const [privateBlendCategoryIds, setPrivateBlendCategoryIds] = useState(new Set());
  const [isUpdatingPrivateBlendCategory, setIsUpdatingPrivateBlendCategory] = useState(false);
  const [isPrivateBlendModalOpen, setIsPrivateBlendModalOpen] = useState(false);

  const siteBaseUrl = (config?.siteUrl || '').replace(/\/$/, '');

  const categoryOptions = useMemo(() => {
    const optionsFromTree = flattenCategoryOptions(categoriesTree);
    if (optionsFromTree.length) {
      return optionsFromTree;
    }
    return (flatCategories || []).map((category) => ({
      id: String(category.id),
      label:
        category.pathNames?.length
          ? category.pathNames.join(' / ')
          : category.name || `Κατηγορία #${category.id}`,
    }));
  }, [categoriesTree, flatCategories]);


  const fetchCoffeeBlends = useCallback(async () => {
    if (isCategoriesOnly) {
      setCoffeeBlends([]);
      setTotalPages(1);
      return;
    }

    setLoading(true);
    try {
      const params = {
        page: currentPage,
        per_page: itemsPerPage,
      };

      const fetchCategoryId = activeCategoryId
        ? String(activeCategoryId)
        : resolvedRootCategoryId
          ? String(resolvedRootCategoryId)
          : null;
      let response;
      let fetchedBlends = [];
      let totalPagesFromResponse = 1;

      const parseProductsResponse = (payload) => {
        if (!payload) return [];
        if (Array.isArray(payload)) return payload;
        if (Array.isArray(payload.products)) return payload.products;
        if (Array.isArray(payload.coffees)) return payload.coffees;
        if (Array.isArray(payload.data)) return payload.data;
        if (Array.isArray(payload.items)) return payload.items;
        return [];
      };

      const extractTotalPages = (payload) =>
        payload?.total_pages ||
        payload?.meta?.last_page ||
        payload?.last_page ||
        1;

      if (fetchCategoryId) {
        try {
          response = await axios.get(`/api/categories/${fetchCategoryId}/products`, { params });
          fetchedBlends = parseProductsResponse(response.data);
          totalPagesFromResponse = extractTotalPages(response.data);
        } catch (categoryError) {
          console.error('Error fetching category products, falling back:', categoryError);
          response = await axios.get('/api/coffee-blends', { params });
          fetchedBlends = parseProductsResponse(response.data);
          totalPagesFromResponse = extractTotalPages(response.data);
        }
      } else {
        response = await axios.get('/api/coffee-blends', { params });
        fetchedBlends = parseProductsResponse(response.data);
        totalPagesFromResponse = extractTotalPages(response.data);
      }

      setCoffeeBlends(fetchedBlends);
      setTotalPages(totalPagesFromResponse);
    } catch (error) {
      console.error('Error fetching coffee blends:', error);
      setCoffeeBlends([]);
    } finally {
      setLoading(false);
    }
  }, [activeCategoryId, currentPage, isCategoriesOnly, resolvedRootCategoryId, rootSlug]);

  useEffect(() => {
    fetchCoffeeBlends();
  }, [fetchCoffeeBlends]);

  const fetchCategoryTree = useCallback(async () => {
    setCategoryLoading(true);
    try {
      const response = await axios.get('/api/categories-full-tree');
      const normalizedTree = normalizeCategoryTreeResponse(response.data)
        .map((node) => normalizeCategoryNode(node))
        .filter(Boolean);

      let scopedTree = normalizedTree;
      let rootNode = null;
      const baseRootId = rootQueryParam || rootCategoryId;

      if (!isCategoriesOnly) {
        if (baseRootId) {
          rootNode = normalizedTree.find(
            (node) => String(node.id) === String(baseRootId)
          );
        }

        if (!rootNode && rootSlug) {
          rootNode = normalizedTree.find(
            (node) =>
              node.slug && node.slug.toLowerCase() === rootSlug.toLowerCase()
          );
        }

        if (rootNode) {
          scopedTree = [rootNode];
          setResolvedRootCategoryId(String(rootNode.id));
        } else if (baseRootId) {
          setResolvedRootCategoryId(String(baseRootId));
        } else if (normalizedTree.length) {
          setResolvedRootCategoryId(String(normalizedTree[0].id));
        }
      } else if (rootQueryParam && rootQueryParam !== resolvedRootCategoryId) {
        setResolvedRootCategoryId(rootQueryParam);
      }

      setCategoriesTree(scopedTree);
      setFlatCategories(flattenTreeToList(scopedTree));
      if (selectedCategoryId && !findCategoryInTree(scopedTree, selectedCategoryId)) {
        setSelectedCategoryId('');
        setActiveCategoryId('');
      }
    } catch (error) {
      console.error('Error fetching category tree:', error);
    } finally {
      setCategoryLoading(false);
    }
  }, [selectedCategoryId, rootCategoryId, rootSlug, resolvedRootCategoryId, isCategoriesOnly]);

  const refreshCategories = useCallback(async () => {
    await fetchCategoryTree();
  }, [fetchCategoryTree]);

  useEffect(() => {
    refreshCategories();
  }, [refreshCategories]);

  useEffect(() => {
    if (typeof window !== 'undefined' && window.matchMedia('(min-width: 1024px)').matches) {
      setIsCategoryPanelOpen(true);
    }
  }, []);

  useEffect(() => {
    if (isCategoriesOnly) {
      setIsCategoryPanelOpen(true);
    }
  }, [isCategoriesOnly]);

  useEffect(() => {
    setSelectedCategoryId('');
    setActiveCategoryId('');
  }, [resolvedRootCategoryId]);

  useEffect(() => {
    if (rootQueryParam && rootQueryParam !== resolvedRootCategoryId) {
      setResolvedRootCategoryId(rootQueryParam);
    }
  }, [rootQueryParam, resolvedRootCategoryId]);

  useEffect(() => {
    if (typeof document === 'undefined') {
      return;
    }
    const shouldLock =
      isCategoryPanelOpen &&
      typeof window !== 'undefined' &&
      !window.matchMedia('(min-width: 1024px)').matches;
    document.body.style.overflow = shouldLock ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [isCategoryPanelOpen]);

  const openCategoryPanel = () => setIsCategoryPanelOpen(true);
  const closeCategoryPanel = () => setIsCategoryPanelOpen(false);
  const toggleCategoryPanel = () => setIsCategoryPanelOpen((prev) => !prev);

  const handleOpenForm = (blend) => {
    setEditingBlend(blend);
    setIsFormOpen(true);
  };

  const handleCloseForm = () => {
    setIsFormOpen(false);
    setEditingBlend(null);
  };

  const handleDelete = (blendId) => {
    setDeleteBlendId(blendId);
  };

  const confirmDelete = async () => {
    try {
      await axios.delete(`/api/coffee-blends/${deleteBlendId}`);
      setCoffeeBlends((prev) => prev.filter((blend) => blend.product_id !== deleteBlendId));
      setDeleteBlendId(null);
    } catch (error) {
      console.error('Failed to delete coffee blend:', error);
      toast({
        title: 'Σφάλμα',
        description: 'Αποτυχία διαγραφής ποικιλίας. Παρακαλώ δοκιμάστε ξανά.',
        variant: 'error',
      });
    }
  };

  const cancelDelete = () => {
    setDeleteBlendId(null);
  };

  const handleViewMedia = (mediaItem) => {
    setSelectedMedia(mediaItem);
    setIsViewDialogOpen(true);
  };

  const handleCloseViewDialog = () => {
    setIsViewDialogOpen(false);
    setSelectedMedia(null);
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage((prev) => prev - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage((prev) => prev + 1);
    }
  };

  const onSaveBasicInfo = async (formData) => {
    try {
      const configRequest = {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      };

      let response;
      if (editingBlend) {
        formData.append('_method', 'PUT');
        response = await axios.post(`/api/products/${editingBlend.product_id}`, formData, configRequest);
      } else {
        response = await axios.post('/api/products', formData, configRequest);
      }

      if (response.status === 200 || response.status === 201) {
        const updatedBlend = response.data;
        setCoffeeBlends((prevBlends) => {
          if (editingBlend) {
            return prevBlends.map((blend) =>
              blend.product_id === editingBlend.product_id ? { ...blend, ...updatedBlend } : blend
            );
          }
          return [...prevBlends, updatedBlend];
        });

        toast({
          title: 'Επιτυχία',
          description: editingBlend ? 'Το blend ενημερώθηκε επιτυχώς' : 'Το νέο blend προστέθηκε',
          variant: 'success',
        });

        handleCloseForm();
      } else {
        throw new Error('Αποτυχία αποθήκευσης blend');
      }
    } catch (error) {
      console.error('Error saving basic info:', error);
      const description = error.response?.data?.message || 'Κάτι πήγε στραβά. Παρακαλώ προσπαθήστε ξανά.';
      toast({
        title: 'Σφάλμα',
        description,
        variant: 'error',
      });
    }
  };

  const onSaveSeoMeta = async (seoMetaData) => {
    try {
      const configRequest = {
        headers: {
          'Content-Type': 'application/json',
        },
      };

      let response;
      if (seoMetaData.seo_meta_id) {
        response = await axios.put(`/api/seo-meta/${seoMetaData.seo_meta_id}`, seoMetaData, configRequest);
      } else {
        response = await axios.post('/api/seo-meta', seoMetaData, configRequest);
      }

      if (!response.data.id) {
        console.error('Response does not contain product_id');
        return;
      }

      setCoffeeBlends((prev) =>
        prev.map((blend) =>
          blend.product_id === seoMetaData.product_id ? { ...blend, seo_meta: response.data } : blend
        )
      );

      handleCloseForm();
    } catch (error) {
      console.error('Error saving SEO meta:', error);
      toast({
        title: 'Σφάλμα',
        description: 'Αποτυχία αποθήκευσης SEO Meta',
        variant: 'error',
      });
    }
  };

  const selectedCategory = useMemo(
    () => findCategoryInTree(categoriesTree, selectedCategoryId),
    [categoriesTree, selectedCategoryId]
  );

  const handleSelectCategory = (category) => {
    const id = category?.id ? String(category.id) : '';
    setSelectedCategoryId(id);
    setActiveCategoryId(id);
    setCurrentPage(1);
  };

  const handleClearCategorySelection = () => {
    setSelectedCategoryId('');
    setActiveCategoryId('');
    setCurrentPage(1);
  };

  const openCreateRootCategory = useCallback(() => {
    setCategoryModalMode('create');
    setCategoryModalInitial(null);
    setCategoryModalParentId(isCategoriesOnly ? null : resolvedRootCategoryId);
    setIsCategoryModalOpen(true);
  }, [isCategoriesOnly, resolvedRootCategoryId]);

  const openCreateChildCategory = (parent) => {
    setCategoryModalMode('create');
    setCategoryModalInitial(null);
    setCategoryModalParentId(parent?.id ? String(parent.id) : null);
    setIsCategoryModalOpen(true);
  };

  const openEditCategoryModal = (category) => {
    setCategoryModalMode('edit');
    setCategoryModalInitial(category);
    setCategoryModalParentId(category?.parent_id ?? null);
    setIsCategoryModalOpen(true);
  };

  const closeCategoryModal = () => {
    setIsCategoryModalOpen(false);
    setCategoryModalInitial(null);
    setCategoryModalParentId(null);
  };

  const handleCategorySubmit = async (payload) => {
    setIsCategorySubmitting(true);
    try {
      if (categoryModalMode === 'edit' && categoryModalInitial) {
        await axios.put(`/api/categories/${categoryModalInitial.id}`, payload);
        toast({ title: 'Επιτυχία', description: 'Η κατηγορία ενημερώθηκε.', variant: 'success' });
      } else {
        await axios.post('/api/categories', payload);
        toast({ title: 'Επιτυχία', description: 'Η κατηγορία δημιουργήθηκε.', variant: 'success' });
      }
      closeCategoryModal();
      await refreshCategories();
    } catch (error) {
      const message = error.response?.data?.message || 'Αποτυχία αποθήκευσης κατηγορίας';
      toast({ title: 'Σφάλμα', description: message, variant: 'error' });
    } finally {
      setIsCategorySubmitting(false);
    }
  };

  const handleDeleteCategory = (category) => {
    if (category?.children?.length) {
      toast({
        title: 'Προειδοποίηση',
        description: 'Δεν μπορείτε να διαγράψετε κατηγορία με υποκατηγορίες.',
        variant: 'default',
      });
      return;
    }
    setCategoryToDelete(category);
  };

  const confirmCategoryDelete = async () => {
    if (!categoryToDelete) return;
    try {
      await axios.delete(`/api/categories/${categoryToDelete.id}`);
      toast({ title: 'Επιτυχία', description: 'Η κατηγορία διαγράφηκε.', variant: 'success' });
      if (String(categoryToDelete.id) === selectedCategoryId) {
        handleClearCategorySelection();
      }
      setCategoryToDelete(null);
      await refreshCategories();
    } catch (error) {
      const message = error.response?.data?.message || 'Αποτυχία διαγραφής κατηγορίας';
      toast({ title: 'Σφάλμα', description: message, variant: 'error' });
    }
  };

  const cancelCategoryDelete = () => {
    setCategoryToDelete(null);
  };

  const hasCategoryFilter = Boolean(activeCategoryId);
  const selectedCategoryPath = selectedCategory?.pathNames?.join(' / ');
  const productFormCategoryId = editingBlend?.category_id
    ? String(editingBlend.category_id)
    : selectedCategoryId || resolvedRootCategoryId || '';
  const rootFilterLabel = useMemo(() => {
    if (pageTitleOverride) return `Όλα τα ${pageTitleOverride.toLowerCase()}`;
    return rootSlug === 'drinks' ? 'Όλα τα ροφήματα' : 'Όλοι οι καφέδες';
  }, [pageTitleOverride, rootSlug]);

  const quickFilterOptions = useMemo(
    () => [
      { id: QUICK_FILTER_ROOT_VALUE, label: rootFilterLabel },
      ...categoryOptions,
    ],
    [categoryOptions, rootFilterLabel]
  );

  const handleQuickFilterChange = useCallback(
    (value) => {
      const nextValue = value === QUICK_FILTER_ROOT_VALUE ? '' : value;
      setSelectedCategoryId(nextValue);
      setActiveCategoryId(nextValue);
      setCurrentPage(1);
    },
    []
  );


  const getProductUrl = (blend) => {
    const slugSegments = getBlendCategoryPath(blend, 'slug');
    const fallbackSlug = blend?.category?.slug ? [blend.category.slug] : [];
    const pathSegments = [...(slugSegments.length ? slugSegments : fallbackSlug), blend?.slug].filter(Boolean);

    if (!pathSegments.length) {
      return siteBaseUrl || '#';
    }

    if (!siteBaseUrl) {
      return '#';
    }

    return `${siteBaseUrl}/${pathSegments.join('/')}`;
  };

  const CategoryPanelBody = ({ showCloseButton = false, onClose }) => (
    <div className="flex h-full flex-col gap-4 overflow-y-auto p-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold">Κατηγορίες</h2>
          <p className="text-xs text-gray-500">
            Επιλέξτε κατηγορία για να εμφανιστούν μόνο τα σχετικά προϊόντα.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={openCreateRootCategory}>
            Νέα
          </Button>
          {showCloseButton && (
            <Button variant="ghost" size="sm" onClick={onClose} title="Κλείσιμο">
              <XMarkIcon className="h-5 w-5" />
            </Button>
          )}
        </div>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 text-sm">
        <div>
          <p className="font-medium text-gray-700">
            {selectedCategory ? `Ενεργή: ${selectedCategory.name}` : 'Ενεργή: Όλες οι κατηγορίες'}
          </p>
          {selectedCategoryPath && (
            <p className="text-xs text-gray-500">{selectedCategoryPath}</p>
          )}
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleClearCategorySelection}
            disabled={!selectedCategoryId}
          >
            Καθαρισμός
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => openCreateChildCategory(selectedCategory)}
            disabled={!selectedCategoryId}
          >
            Νέα υποκατηγορία
          </Button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto pr-2">
        {categoryLoading ? (
          <div className="space-y-2">
            <SkeletonLoader />
            <SkeletonLoader />
          </div>
        ) : categoriesTree.length ? (
          categoriesTree.map((node) => (
            <CategoryTreeNode
              key={node.id}
              node={node}
              selectedId={selectedCategoryId}
              onSelect={handleSelectCategory}
              onAddChild={openCreateChildCategory}
              onEdit={openEditCategoryModal}
              onDelete={handleDeleteCategory}
            />
          ))
        ) : (
          <p className="text-sm text-gray-500">Δεν υπάρχουν κατηγορίες. Δημιουργήστε την πρώτη.</p>
        )}
      </div>

      {selectedCategory && (
        <div className="rounded-lg border border-gray-200 p-4 text-sm">
          <div className="flex items-center justify-between">
            <p className="font-semibold text-gray-800">{selectedCategory.name}</p>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => openEditCategoryModal(selectedCategory)}>
                Επεξεργασία
              </Button>
              <Button variant="outline" size="sm" onClick={() => handleDeleteCategory(selectedCategory)}>
                Διαγραφή
              </Button>
            </div>
          </div>
          {selectedCategory.media?.full_file_path && (
            <img
              src={selectedCategory.media.full_file_path}
              alt={selectedCategory.name}
              className="mt-3 h-32 w-full rounded object-cover"
            />
          )}
          {selectedCategory.description?.small_description && (
            <p className="mt-3 text-gray-600">{selectedCategory.description.small_description}</p>
          )}
          {selectedCategory.description?.large_description && (
            <div
              className="prose prose-sm mt-3 max-w-none text-gray-700"
              dangerouslySetInnerHTML={{ __html: selectedCategory.description.large_description }}
            />
          )}
        </div>
      )}
    </div>
  );

  const defaultTitle = rootSlug === 'drinks' ? 'Ροφήματα' : 'Καφές';
  const defaultDescription =
    rootSlug === 'drinks'
      ? 'Διαχειριστείτε τα ροφήματα και τις υποκατηγορίες τους.'
      : 'Διαχειριστείτε τον καφέ και τις υποκατηγορίες του.';

  const loadPrivateBlendCategories = useCallback(async () => {
    try {
      const response = await axios.get('/api/private-blend/categories');
      const ids =
        response?.data?.category_ids ||
        response?.data?.categories?.map((c) => c.id ?? c.category_id) ||
        [];
      setPrivateBlendCategoryIds(new Set((ids || []).map((id) => String(id))));
    } catch (error) {
      console.error('Failed to load private blend categories', error);
    }
  }, []);

  useEffect(() => {
    loadPrivateBlendCategories();
  }, [loadPrivateBlendCategories]);

  const handleTogglePrivateBlendCategory = useCallback(
    async (categoryId, isChecked) => {
      if (!categoryId) return;
      const idStr = String(categoryId);
      const nextSelection = new Set(privateBlendCategoryIds);
      if (isChecked) {
        nextSelection.add(idStr);
      } else {
        nextSelection.delete(idStr);
      }
      setPrivateBlendCategoryIds(nextSelection);
      setIsUpdatingPrivateBlendCategory(true);

      try {
        await axios.post('/api/private-blend/categories', {
          category_ids: Array.from(nextSelection),
        });
        toast({
          title: 'Επιτυχία',
          description: 'Η κατηγορία ενημερώθηκε για το Private Blend.',
          variant: 'success',
        });
      } catch (error) {
        console.error('Failed to update private blend categories', error);
        toast({
          title: 'Σφάλμα',
          description: 'Δεν ήταν δυνατή η ενημέρωση της κατηγορίας.',
          variant: 'error',
        });
        setPrivateBlendCategoryIds((prev) => {
          const rollback = new Set(prev);
          if (isChecked) {
            rollback.delete(idStr);
          } else {
            rollback.add(idStr);
          }
          return rollback;
        });
      } finally {
        setIsUpdatingPrivateBlendCategory(false);
      }
    },
    [privateBlendCategoryIds]
  );

  const targetRootCategoryId = resolvedRootCategoryId || rootCategoryId || '';

  const pageTitle = isCategoriesOnly ? 'Κατηγορίες' : pageTitleOverride || defaultTitle;
  const pageDescription = pageDescriptionOverride || defaultDescription;

  return (
    <div className="container mx-auto px-4 py-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">{pageTitle}</h1>
          <p className="text-sm text-gray-500">{pageDescription}</p>
        </div>
        {!isCategoriesOnly && (
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={toggleCategoryPanel}>
              {isCategoryPanelOpen ? 'Απόκρυψη κατηγοριών' : 'Κατηγορίες'}
            </Button>
            <Button onClick={() => handleOpenForm(null)}>Νέο προϊόν</Button>
            {targetRootCategoryId && (
              <Button
                variant="outline"
                size="icon"
                onClick={() => setIsPrivateBlendModalOpen(true)}
                aria-label="Ρυθμίσεις Private Blend"
              >
                <EllipsisVerticalIcon className="h-5 w-5" />
              </Button>
            )}
          </div>
        )}
      </div>

      {!isCategoriesOnly && hasCategoryFilter && (
        <div className="mt-4 flex flex-wrap items-center justify-between gap-3 rounded-md border-l-4 border-emerald-500 bg-emerald-50 p-3 text-sm text-emerald-900">
          <p>
            Φίλτρο κατηγορίας: <strong>{selectedCategoryPath || selectedCategory?.name}</strong>
          </p>
          <Button variant="outline" size="sm" onClick={handleClearCategorySelection}>
            Καθαρισμός
          </Button>
        </div>
      )}

      <div className={`mt-6 ${isCategoriesOnly ? 'rounded-lg border border-gray-200 bg-white p-0' : ''}`}>
        {isCategoriesOnly ? (
          <CategoryPanelBody />
        ) : (
          <>
            <div className="mb-6 flex flex-wrap items-center gap-3 rounded-md bg-white p-4 shadow-sm">
            <div>
              <p className="text-sm font-semibold text-gray-700">Φίλτρο κατηγορίας</p>
              <p className="text-xs text-gray-500">
                Επιλέξτε μια κατηγορία για να εμφανιστούν μόνο τα σχετικά προϊόντα.
              </p>
            </div>
            <div className="flex-1 min-w-[220px]">
              <Select
                value={activeCategoryId || QUICK_FILTER_ROOT_VALUE}
                onValueChange={handleQuickFilterChange}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Επιλέξτε κατηγορία" />
                </SelectTrigger>
                <SelectContent>
                  {quickFilterOptions.map((option) => (
                    <SelectItem key={option.id || 'root'} value={option.id}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {hasCategoryFilter && (
              <Button variant="outline" size="sm" onClick={handleClearCategorySelection}>
                Καθαρισμός
              </Button>
            )}
          </div>
          <div className="flex flex-col gap-6 lg:flex-row lg:items-start">
            <aside
              className={`hidden overflow-hidden rounded-lg border border-gray-200 bg-white transition-all duration-300 ease-in-out lg:block ${
                isCategoryPanelOpen
                  ? 'lg:max-w-sm lg:w-80 lg:opacity-100 lg:scale-100 lg:pointer-events-auto'
                  : 'lg:max-w-[0px] lg:w-0 lg:opacity-0 lg:scale-95 lg:pointer-events-none'
            }`}
            >
              {isCategoryPanelOpen && <CategoryPanelBody />}
            </aside>

            <section className="flex flex-1 flex-col rounded-lg border border-gray-200 bg-white p-4 transition-all duration-300 ease-in-out">
            {loading ? (
              <div className="space-y-3">
                <SkeletonLoader />
                <SkeletonLoader />
                <SkeletonLoader />
              </div>
            ) : coffeeBlends.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Εικόνα</TableHead>
                  <TableHead>Όνομα</TableHead>
                  <TableHead>Κατηγορία</TableHead>
                  <TableHead className="text-center">Ορατότητα</TableHead>
                  <TableHead>Επεξεργασία/Διαγραφή</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {coffeeBlends.map((blend) => {
                  const productUrl = getProductUrl(blend);
                  const categoryLabel = getCategoryDisplayPath(blend);
                  return (
                    <TableRow key={blend.product_id}>
                      <TableCell>
                        {blend.media?.full_file_path ? (
                          <img
                            onClick={() => handleViewMedia(blend.media)}
                            src={blend.media.full_file_path}
                            alt={blend.name || 'Coffee Blend'}
                            className="h-16 w-16 cursor-pointer rounded-md object-cover"
                          />
                        ) : (
                          <span className="text-gray-400">Χωρίς εικόνα</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center space-x-2">
                          <a
                            href={productUrl}
                            className="flex items-center text-blue-500 hover:underline"
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {blend.name}
                            <ArrowTopRightOnSquareIcon className="ml-1 h-4 w-4" aria-label="Open front-end page" />
                          </a>
                        </div>
                      </TableCell>
                      <TableCell>
                        {categoryLabel ? (
                          <span>{categoryLabel}</span>
                        ) : (
                          <span className="text-gray-400">Χωρίς κατηγορία</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center justify-center">
                          {blend.visibility === 'public' ? (
                            <EyeIcon className="h-5 w-5 text-green-500" aria-label="Δημόσιο" />
                          ) : (
                            <EyeSlashIcon className="h-5 w-5 text-red-500" aria-label="Προσχέδιο" />
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button variant="outline" size="sm" onClick={() => handleOpenForm(blend)} className="mr-2">
                          <PencilIcon className="h-5 w-5 text-blue-500" />
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => handleDelete(blend.product_id)}>
                          <TrashIcon className="h-5 w-5 text-red-500" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          ) : (
            <div className="py-4 text-center">Δεν βρέθηκαν ποικιλίες καφέ</div>
          )}

          <div className="mt-4 flex items-center justify-between">
            <Button onClick={handlePreviousPage} disabled={currentPage === 1}>
              Προηγούμενο
            </Button>
            <span>
              Σελίδα {currentPage} από {totalPages}
            </span>
            <Button onClick={handleNextPage} disabled={currentPage === totalPages}>
              Επόμενο
            </Button>
          </div>
            </section>
          </div>
        </>
        )}
      </div>

      {!isCategoriesOnly && !isCategoryPanelOpen && (
        <div className="fixed left-4 bottom-4 z-40">
          <Button onClick={openCategoryPanel} className="shadow-lg">
            Κατηγορίες
          </Button>
        </div>
      )}

      {!isCategoriesOnly && isCategoryPanelOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/40 backdrop-blur-sm lg:hidden"
          onClick={closeCategoryPanel}
        />
      )}
      {!isCategoriesOnly && (
        <div
          className={`fixed inset-y-0 left-0 z-50 w-full max-w-md transform border-r border-gray-200 bg-white shadow-xl transition-transform duration-300 lg:hidden ${
            isCategoryPanelOpen ? 'translate-x-0' : '-translate-x-full'
          }`}
          role="dialog"
          aria-label="Πίνακας κατηγοριών"
          aria-hidden={!isCategoryPanelOpen}
        >
          <CategoryPanelBody showCloseButton onClose={closeCategoryPanel} />
        </div>
      )}

      {!isCategoriesOnly && (
        <>
          {targetRootCategoryId && (
            <Dialog open={isPrivateBlendModalOpen} onOpenChange={setIsPrivateBlendModalOpen}>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Private Blend</DialogTitle>
                  <DialogDescription>
                    Διαχειριστείτε την κατηγορία {pageTitle}.
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-3">
                  <label className="flex items-center gap-3 rounded-md border px-3 py-2">
                    <Checkbox
                      checked={privateBlendCategoryIds.has(String(targetRootCategoryId))}
                      disabled={isUpdatingPrivateBlendCategory}
                      onCheckedChange={(value) =>
                        handleTogglePrivateBlendCategory(targetRootCategoryId, Boolean(value))
                      }
                    />
                    <span className="text-sm text-gray-800">
                      Συμπερίληψη κορυφαίας κατηγορίας στο Private Blend
                    </span>
                  </label>
                </div>
              </DialogContent>
            </Dialog>
          )}
          <ProductForm
            isOpenProductForm={isFormOpen}
            onCloseProductForm={handleCloseForm}
            onSaveBasicInfo={onSaveBasicInfo}
            onSaveSeoMeta={onSaveSeoMeta}
            initialValues={editingBlend}
            parentId={rootCategoryId}
            categoryOptions={categoryOptions}
            defaultCategoryId={productFormCategoryId}
            onOpenCategoryPanel={openCategoryPanel}
          />

          <DeleteConfirmationDialog
            isOpen={!!deleteBlendId}
            onClose={cancelDelete}
            onDelete={confirmDelete}
            confirmationTitle="Delete Coffee Blend"
            confirmationMessage={`Are you sure you want to delete the coffee blend "${coffeeBlends?.find((b) => b.product_id === deleteBlendId)?.name}"?`}
          />

          <MediaViewDialog isOpen={isViewDialogOpen} onClose={handleCloseViewDialog} mediaItem={selectedMedia} />
        </>
      )}

      <DeleteConfirmationDialog
        isOpen={!!categoryToDelete}
        onClose={cancelCategoryDelete}
        onDelete={confirmCategoryDelete}
        confirmationTitle="Διαγραφή κατηγορίας"
        confirmationMessage={`Είστε σίγουροι ότι θέλετε να διαγράψετε την κατηγορία "${categoryToDelete?.name}";`}
      />

      <CategoryModal
        isOpen={isCategoryModalOpen}
        mode={categoryModalMode}
        onClose={closeCategoryModal}
        onSubmit={handleCategorySubmit}
        parentId={categoryModalParentId}
        initialValues={categoryModalInitial}
        categories={flatCategories}
        isSubmitting={isCategorySubmitting}
        rootCategoryId={rootCategoryId}
        allowRootSelection={isCategoriesOnly}
      />

    </div>
  );
};

export default CoffeeBlends;
